// Login — Google OAuth (ADR-008) + an offline dev role-picker (AUTH_MODE=dev).
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth.js";
import { useDevSnapshots } from "../hooks/useDevSnapshots.js";
import { useDevUsersForSnapshot } from "../hooks/useDevUsers.js";
import { userRoleSubtitle } from "../lib/rbacUi.js";
import { Button, Card, CardBody, CardHeader, Select, Spinner } from "../components/ui.js";

const DEFAULT_SNAPSHOT = "demo-fictional";

export function Login(): JSX.Element {
  const { devLogin } = useAuth();
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: snapshotMeta, isLoading: snapshotsLoading } = useDevSnapshots();
  const snapshots = snapshotMeta?.snapshots ?? [];
  const [dataset, setDataset] = useState<string | null>(null);

  useEffect(() => {
    if (!snapshots.length || dataset) return;
    const preferred =
      snapshotMeta?.active ??
      snapshots.find((s) => s.id === DEFAULT_SNAPSHOT)?.id ??
      snapshots[0]?.id ??
      null;
    setDataset(preferred);
  }, [snapshots, snapshotMeta?.active, dataset]);

  const { data: devUsers, isLoading: usersLoading } = useDevUsersForSnapshot(dataset);
  const devAvailable = snapshots.length > 0;
  // Only block on dataset/users once snapshots exist — an empty list must not spin forever.
  const loading = snapshotsLoading || (devAvailable && (!dataset || usersLoading));

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-xl font-bold text-brand">
            RR<span className="text-slate-400">\</span>ID — Reviewer Selection
          </h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to continue.</p>
        </CardHeader>
        <CardBody className="space-y-5">
          <a href="/api/auth/google" className="block">
            <Button className="w-full" variant="primary">
              Sign in with Google
            </Button>
          </a>

          <div className="relative text-center">
            <span className="bg-white px-2 text-xs uppercase tracking-wide text-slate-400">or (dev only)</span>
            <div className="absolute inset-x-0 top-1/2 -z-10 h-px bg-slate-200" />
          </div>

          {loading ? (
            <Spinner label="Loading dev datasets…" />
          ) : devAvailable ? (
            <div className="space-y-3">
              <label className="block space-y-1">
                <span className="text-xs font-medium text-slate-600">Dataset</span>
                <Select
                  value={dataset ?? ""}
                  onChange={(e) => {
                    setDataset(e.target.value);
                    setPicked("");
                  }}
                  className="w-full"
                >
                  {snapshots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </Select>
                {dataset && (
                  <p className="text-xs text-slate-500">
                    {snapshots.find((s) => s.id === dataset)?.description}
                  </p>
                )}
              </label>

              <label className="block space-y-1">
                <span className="text-xs font-medium text-slate-600">Persona</span>
                <Select value={picked} onChange={(e) => setPicked(e.target.value)} className="w-full">
                  <option value="">Pick a role to impersonate…</option>
                  {(devUsers ?? []).map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name} — {userRoleSubtitle(u)}
                    </option>
                  ))}
                </Select>
              </label>

              <Button
                className="w-full"
                variant="secondary"
                disabled={!picked || !dataset || busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await devLogin(picked, dataset ?? undefined);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "Signing in…" : "Enter as this user"}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              No dev datasets in this deployment (demo snapshots were not baked into the image). Use{" "}
              <code>just gcp-wt-deploy</code> from a worktree checkout that has{" "}
              <code>data-samples/snapshots/*-demo-*</code>, or sign in with Google above.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
